import React from 'react';
import { Flex, Box, Text, Button, Dialog, DialogTrigger, DialogContent, DialogFooter, TextField, Select } from '@chatsdet/ui';
import type { UseMutationResult } from '@tanstack/react-query';
import type { Schedule, IntervalUnit, ScheduleUpdateData } from './types.js';
import type { SimpleScheduleConfig, IntervalScheduleConfig, CronScheduleConfig } from './types.js';
import {
  intervalSecondsFromUnit,
  bestIntervalUnit,
  intervalUnitOptions,
  enabledOptions,
} from './types.js';
import { JobConfigSection } from './JobConfigSection.js';
import { PayloadEditor } from './PayloadEditor.js';
import { ErrorBanner } from './ErrorBanner.js';
import { CronPatternPresets, TimezonePresets } from './CronPresetButtons.js';
import { useCronValidation } from './useCronValidation.js';
import { buildCronScheduleConfig } from './cronScheduleConfig.js';

type EditScheduleDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: Schedule | null;
  onScheduleCleared: () => void;
  updateMutation: UseMutationResult<unknown, Error, { id: string; data: ScheduleUpdateData }>;
};

export function EditScheduleDialog({
  isOpen,
  onOpenChange,
  schedule,
  onScheduleCleared,
  updateMutation,
}: EditScheduleDialogProps) {
  const [editName, setEditName] = React.useState('');
  const [editNextRunAt, setEditNextRunAt] = React.useState('');
  const [editEnabled, setEditEnabled] = React.useState<'yes' | 'no'>('yes');
  const [editIntervalValue, setEditIntervalValue] = React.useState('');
  const [editIntervalUnit, setEditIntervalUnit] = React.useState<IntervalUnit>('minutes');
  const [editTime, setEditTime] = React.useState('');
  const [editTimezone, setEditTimezone] = React.useState('');
  const [editCronPattern, setEditCronPattern] = React.useState('');
  const [editCronTimezone, setEditCronTimezone] = React.useState('');
  const [editPayload, setEditPayload] = React.useState('');
  const [editError, setEditError] = React.useState<string | null>(null);

  const isPending = updateMutation.isPending;
  // The saved cron config, cast once and reused below.
  const cronConfig = schedule?.scheduleType === 'cron' ? (schedule.scheduleConfig as CronScheduleConfig) : null;
  // What would actually be submitted if the timezone field is blank. Computed once
  // and passed to both useCronValidation and buildCronScheduleConfig below so the
  // preview always describes exactly the config that would be saved.
  const effectiveCronTimezone = editCronTimezone || cronConfig?.timezone || '';
  const cronValidation = useCronValidation({
    pattern: editCronPattern,
    timezone: effectiveCronTimezone,
    enabled: schedule?.scheduleType === 'cron',
  });
  // True only when the pattern/timezone fields actually differ from the saved
  // schedule — opening Edit re-validates the saved pattern on mount, and without
  // this a rename or Enabled toggle would sit blocked behind that ~1s round-trip
  // even though the cron config isn't changing at all. Compares the raw field value
  // (not effectiveCronTimezone) so a blank timezone field alone isn't "dirty".
  const cronDirty = Boolean(
    cronConfig &&
      ((editCronPattern && editCronPattern !== cronConfig.pattern) ||
        (editCronTimezone && editCronTimezone !== cronConfig.timezone))
  );
  // Blocks submit while cron validation is pending or failed for the edited pattern,
  // but only when the cron fields actually changed, so renaming or toggling Enabled
  // is never held up by validating an unmodified saved pattern.
  const cronBlocksSubmit =
    schedule?.scheduleType === 'cron' &&
    cronDirty &&
    Boolean(editCronPattern) &&
    (!cronValidation.settled || Boolean(cronValidation.error) || cronValidation.preview.length === 0);

  // Backdrop click / Escape close the dialog via DialogTrigger's onOpenChange directly,
  // bypassing Cancel/handleClose — route everything through here so every close path
  // clears editingSchedule (otherwise reopening Edit on the same row won't re-run the
  // populate effect below, since `schedule` hasn't changed, and stale edits linger).
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      onScheduleCleared();
    }
  };

  // Populate form when schedule changes
  React.useEffect(() => {
    if (!schedule) return;

    setEditName(schedule.name || '');
    setEditEnabled(schedule.enabled ? 'yes' : 'no');
    setEditPayload(JSON.stringify(schedule.payload || {}, null, 2));
    setEditError(null);

    if (schedule.nextRunAt) {
      const date = new Date(schedule.nextRunAt);
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setEditNextRunAt(localDateTime);
    } else {
      setEditNextRunAt('');
    }

    if (schedule.scheduleType === 'interval') {
      const config = schedule.scheduleConfig as IntervalScheduleConfig;
      const { value, unit } = bestIntervalUnit(config.intervalMs / 1000);
      setEditIntervalValue(String(value));
      setEditIntervalUnit(unit);
    } else if (schedule.scheduleType === 'simple') {
      const config = schedule.scheduleConfig as SimpleScheduleConfig;
      setEditTime(config.time);
      setEditTimezone(config.timezone);
    } else if (schedule.scheduleType === 'cron') {
      const config = schedule.scheduleConfig as CronScheduleConfig;
      setEditCronPattern(config.pattern);
      setEditCronTimezone(config.timezone);
    }
  }, [schedule]);

  const handleUpdate = () => {
    if (!schedule) return;

    setEditError(null);

    const updates: ScheduleUpdateData = {};

    if (editName.trim() && editName !== schedule.name) {
      updates.name = editName.trim();
    }

    if (editNextRunAt) {
      const nextRunDate = new Date(editNextRunAt);
      if (!isNaN(nextRunDate.getTime())) {
        updates.nextRunAt = nextRunDate;
      }
    }

    const isEnabled = editEnabled === 'yes';
    if (isEnabled !== schedule.enabled) {
      updates.enabled = isEnabled;
    }

    // Check payload changes
    try {
      const newPayload = JSON.parse(editPayload);
      const currentPayload = schedule.payload || {};
      if (JSON.stringify(newPayload) !== JSON.stringify(currentPayload)) {
        updates.payload = newPayload;
      }
    } catch {
      // Invalid JSON — skip payload update, user will see raw JSON in the editor
    }

    // Check schedule config changes
    if (schedule.scheduleType === 'interval') {
      const currentConfig = schedule.scheduleConfig as IntervalScheduleConfig;
      const intervalValue = parseFloat(editIntervalValue);

      if (intervalValue) {
        const newIntervalMs = intervalSecondsFromUnit(intervalValue, editIntervalUnit) * 1000;
        if (newIntervalMs !== currentConfig.intervalMs && newIntervalMs >= 60000) {
          updates.scheduleType = 'interval';
          updates.scheduleConfig = { intervalMs: newIntervalMs };
        }
      }
    } else if (schedule.scheduleType === 'simple') {
      const currentConfig = schedule.scheduleConfig as SimpleScheduleConfig;
      if (
        (editTime && editTime !== currentConfig.time) ||
        (editTimezone && editTimezone !== currentConfig.timezone)
      ) {
        updates.scheduleType = 'simple';
        updates.scheduleConfig = {
          time: editTime || currentConfig.time,
          timezone: editTimezone || currentConfig.timezone,
        };
      }
    } else if (schedule.scheduleType === 'cron' && cronDirty) {
      // No `|| cronConfig.pattern` fallback — buildCronScheduleConfig already errors
      // on a blank pattern. effectiveCronTimezone is the same value already passed
      // to useCronValidation above, so this submits exactly what was previewed.
      const result = buildCronScheduleConfig(editCronPattern, effectiveCronTimezone, cronValidation);
      if ('config' in result) {
        updates.scheduleType = 'cron';
        updates.scheduleConfig = result.config;
      } else {
        if ('error' in result) setEditError(result.error);
        return;
      }
    }

    if (Object.keys(updates).length > 0) {
      updateMutation.mutate(
        { id: schedule.id, data: updates },
        {
          onSuccess: () => {
            handleOpenChange(false);
          },
        }
      );
    } else {
      handleOpenChange(false);
    }
  };

  const handleClose = () => {
    handleOpenChange(false);
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <span />
      <Dialog title="Edit Schedule">
        <DialogContent>
          <Flex direction="column" gap="md">
            {schedule && (
              <Box>
                <Text size="xs" className="text-muted-foreground mb-1">
                  Schedule ID
                </Text>
                <Text size="sm" className="font-mono text-muted-foreground">
                  {schedule.id}
                </Text>
              </Box>
            )}

            <Select
              label="Enabled"
              items={enabledOptions}
              selectedKey={editEnabled}
              onSelectionChange={(key) => setEditEnabled(key as 'yes' | 'no')}
              isDisabled={isPending}
            />

            <TextField
              label="Schedule Name"
              placeholder="e.g., Daily backup job"
              value={editName}
              onChange={setEditName}
              isDisabled={isPending}
            />

            {schedule?.scheduleType === 'interval' && (
              <Box>
                <Flex gap="md" align="end">
                  <Box className="flex-1">
                    <TextField
                      label="Interval"
                      type="number"
                      placeholder="e.g., 5"
                      value={editIntervalValue}
                      onChange={setEditIntervalValue}
                      isDisabled={isPending}
                    />
                  </Box>
                  <Box className="flex-1">
                    <Select
                      label="Unit"
                      items={intervalUnitOptions}
                      selectedKey={editIntervalUnit}
                      onSelectionChange={(key) => setEditIntervalUnit(key as IntervalUnit)}
                      isDisabled={isPending}
                    />
                  </Box>
                </Flex>
                <Text size="xs" className="text-muted-foreground mt-1">
                  Min: 1 minute, Max: 30 days
                </Text>
              </Box>
            )}

            {schedule?.scheduleType === 'simple' && (
              <>
                <TextField
                  label="Time"
                  type="time"
                  value={editTime}
                  onChange={setEditTime}
                  isDisabled={isPending}
                  description="Time of day to run (HH:mm format)"
                />
                <TextField
                  label="Timezone"
                  placeholder="e.g., America/New_York"
                  value={editTimezone}
                  onChange={setEditTimezone}
                  isDisabled={isPending}
                />
              </>
            )}

            {schedule?.scheduleType === 'cron' && (
              <>
                <Box>
                  <TextField
                    label="Cron Pattern"
                    placeholder="e.g., 0 */6 * * * (every 6 hours)"
                    value={editCronPattern}
                    onChange={setEditCronPattern}
                    isDisabled={isPending}
                    description="Standard cron expression (minute hour day month weekday)"
                  />
                  <CronPatternPresets onSelect={setEditCronPattern} disabled={isPending} />
                </Box>
                <TextField
                  label="Timezone"
                  value={editCronTimezone}
                  onChange={setEditCronTimezone}
                  isDisabled={isPending}
                  description={
                    <>
                      <a
                        href="https://en.wikipedia.org/wiki/List_of_tz_database_time_zones"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        IANA timezone
                      </a>
                      {' (e.g., America/New_York, UTC)'}
                    </>
                  }
                />
                <Box>
                  <TimezonePresets onSelect={setEditCronTimezone} disabled={isPending} />
                </Box>
                {cronValidation.validating && (
                  <Box>
                    <Text size="sm" className="text-muted-foreground">
                      Validating pattern...
                    </Text>
                  </Box>
                )}
                {cronValidation.settled && cronValidation.preview.length > 0 && (
                  <Box className="space-y-1">
                    <Text size="xs" weight="medium" className="mb-2 block">
                      Next 5 scheduled runs:
                    </Text>
                    <div className="space-y-1">
                      {cronValidation.preview.map((time, i) => (
                        <Text key={i} size="sm" className="font-mono text-muted-foreground">
                          {new Date(time).toLocaleString()}
                        </Text>
                      ))}
                    </div>
                  </Box>
                )}
                {cronValidation.settled && cronValidation.error && (
                  <ErrorBanner message={cronValidation.error} />
                )}
              </>
            )}

            <TextField
              label="Next Run Time"
              type="datetime-local"
              value={editNextRunAt}
              onChange={setEditNextRunAt}
              isDisabled={isPending}
              description="Adjust when this schedule should run next"
            />

            {schedule && (
              <JobConfigSection
                jobType={schedule.jobType}
                payloadJson={editPayload}
                onPayloadChange={(payload) => setEditPayload(JSON.stringify(payload, null, 2))}
                disabled={isPending}
              />
            )}

            <PayloadEditor value={editPayload} onChange={setEditPayload} disabled={isPending} />

            {editError && <ErrorBanner message={editError} />}
            {updateMutation.isError && (
              <ErrorBanner
                message={
                  updateMutation.error instanceof Error
                    ? updateMutation.error.message
                    : 'Failed to update schedule'
                }
              />
            )}
          </Flex>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={handleClose} isDisabled={isPending}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleUpdate} isDisabled={isPending || cronBlocksSubmit}>
            {isPending ? 'Updating...' : 'Update Schedule'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
